import {
  StatusBadge,
  CreatedByCell,
  GoalPerformanceCell,
  TagsCell,
  ActionDropdown,
  TitleCell,
  EnrollingCountCell,
} from './list-cell-renderers';
import { formatDateOrDash, parseUtcDate } from '../utils/cadence-data-mappers';

/**
 * Build MUI DataGrid column definitions for the Outreach Plans table.
 * Default visible: title, type, status, createdBy, totalContacts, goalMetric, actions.
 * Hidden by default (optional): cadenceShell, createdDate, lastUpdated, automation,
 *   avgDeliveryRate, avgBounceRate, avgUnsubscribeRate, avgOpenRate, avgClickRate, avgReplyRate, tags.
 */
export const createOutreachPlansColumns = ({ currentUserId, actions, renamingState }) => [
  {
    field: 'title',
    headerName: 'Title',
    flex: 1,
    minWidth: 200,
    sortable: true,
    renderCell: ({ row }) => (
      <TitleCell
        row={row}
        icon="cadences"
        iconKit="gt2"
        onView={(id) => actions.onView('cadence', id)}
        renamingState={renamingState}
      />
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 0.6,
    minWidth: 100,
    sortable: true,
    type: 'singleSelect',
    valueOptions: ['Cadence', 'Prospect Plan'],
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 90,
    sortable: true,
    type: 'singleSelect',
    valueOptions: ['Active', 'Draft', 'Paused', 'Archived'],
    renderCell: ({ row }) => <StatusBadge status={row.status} />,
  },
  {
    field: 'cadenceShell',
    headerName: 'Cadence Blueprint',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    renderCell: ({ row }) => {
      const shellId = row._raw?.sourceCadenceTemplateId;
      if (!row.cadenceShell || !shellId) return '\u2014';
      return (
        <button
          type="button"
          className="cadences-v2-table--name-link"
          onClick={() => actions.onView('template', shellId)}
        >
          {row.cadenceShell}
        </button>
      );
    },
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueGetter: (value) => value?.name || '',
    renderCell: ({ row }) => <CreatedByCell createdBy={row.createdBy} currentUserId={currentUserId} />,
  },
  {
    field: 'createdDate',
    headerName: 'Creation Date',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: parseUtcDate,
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'lastUpdated',
    headerName: 'Last Updated',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: parseUtcDate,
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'totalContacts',
    headerName: '# Current Contacts',
    flex: 0.6,
    minWidth: 100,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row, value }) => <EnrollingCountCell value={value} status={row.status} />,
  },
  {
    field: 'goalMetric',
    headerName: 'Goal Performance',
    flex: 1,
    minWidth: 200,
    sortable: true,
    renderCell: ({ row }) => (
      <GoalPerformanceCell goalName={row.goal} metric={row.goalMetric} target={row.goalTarget} />
    ),
  },
  {
    field: 'automation',
    headerName: 'Automation',
    flex: 0.8,
    minWidth: 180,
    sortable: true,
    valueFormatter: (value) => value || '\u2014',
    renderCell: ({ value }) => (
      <div style={{ padding: '0 12px', whiteSpace: 'normal', lineHeight: 1.4 }}>{value || '\u2014'}</div>
    ),
  },
  {
    field: 'avgDeliveryRate',
    headerName: 'Avg. Email Delivery Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgBounceRate',
    headerName: 'Avg. Email Bounce Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgUnsubscribeRate',
    headerName: 'Avg. Email Unsubscribe Rate',
    flex: 0.7,
    minWidth: 130,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgOpenRate',
    headerName: 'Avg. Email Open Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgClickRate',
    headerName: 'Avg. Email Click Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgReplyRate',
    headerName: 'Avg. Email Reply Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'tags',
    headerName: 'Tags',
    flex: 0.8,
    minWidth: 140,
    sortable: false,
    valueGetter: (value, row) => (row._raw?.tags || []).join(', '),
    renderCell: ({ row }) => <TagsCell tags={row._raw?.tags} />,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    sortable: false,
    filterable: false,
    hideable: false,
    type: 'actions',
    getApplyQuickFilterFn: () => null,
    renderCell: ({ row }) => (
      <ActionDropdown type="cadence" row={row} actions={actions} currentUserId={currentUserId} />
    ),
  },
];
